import { useQuery } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { ArrowLeft, FileText } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import type { ApiError } from '@/lib/api'
import { api } from '@/lib/api'
import { localizedName } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { useT } from '@/lib/i18n'
import { usePageTitle } from '@/lib/usePageTitle'
import type { StaticPage } from '@/shared/types'
import { Markdown } from '@/components/Markdown'
import {
  StorefrontCard,
  StorefrontEmptyState,
  StorefrontPage,
} from '@/components/StorefrontPage'

interface PublicSettings {
  shop_name: string
}

export function StaticPagePage() {
  const { slug } = useParams({ from: '/pages/$slug' })
  const lang = useAppStore((s) => s.lang)
  const T = useT('page')
  const Tp = useT('product')

  const { data: page, isError } = useQuery<StaticPage, ApiError>({
    queryKey: ['page', slug],
    queryFn: () => api.get<StaticPage>(`/pages/${slug}`),
    retry: false,
  })

  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })

  const title = page ? localizedName(lang, page.title_uk, page.title_en) : ''
  const shopName = (settings?.shop_name ?? '').toLowerCase()
  const isMayParfum = shopName.includes('mayparfum')
  const isKlodchik = shopName.includes('klodchik') || shopName.includes('заточ') || shopName.includes('sharp')
  usePageTitle(page ? (settings?.shop_name ? `${title} — ${settings.shop_name}` : title) : undefined)

  if (isError) {
    return (
      <StorefrontPage backTo="/catalog" backLabel={Tp.back}>
        <StorefrontEmptyState
          icon={FileText}
          title={T.notFound}
        />
      </StorefrontPage>
    )
  }

  if (!page) return null

  const content = localizedName(lang, page.content_uk, page.content_en) || page.content_uk

  if (isKlodchik) {
    return (
      <article className="min-h-screen bg-[#15181c] px-5 pb-24 pt-6 text-[#e8e8e8] md:px-8">
        <div className="mx-auto max-w-3xl">
          <Link to="/" className="inline-flex items-center text-sm font-semibold text-[#c9a24b] hover:underline">
            <ArrowLeft className="mr-1 h-4 w-4" />
            {Tp.back}
          </Link>
          <h1 className="mt-6 text-2xl font-bold leading-tight text-white md:text-4xl">{title}</h1>
          <Markdown content={content} className="mt-6 text-sm leading-7 text-[#aab0b8] space-y-4" />
        </div>
      </article>
    )
  }

  if (isMayParfum) {
    return (
      <article className="min-h-screen bg-[#17120f] px-5 pb-24 pt-6 text-[#f7efe3] md:px-8">
        <div className="mx-auto max-w-3xl">
          <Link to="/" className="inline-flex items-center text-sm font-semibold text-[#d7b171]">
            <ArrowLeft className="mr-1 h-4 w-4" />
            {Tp.back}
          </Link>
          <h1 className="mt-6 text-3xl font-semibold leading-tight text-white md:text-5xl">{title}</h1>
          <Markdown content={content} className="mt-6 text-base leading-8 text-[#d8cabb] space-y-4" />
        </div>
      </article>
    )
  }

  return (
    <StorefrontPage
      backTo="/catalog"
      backLabel={Tp.back}
      title={title}
      className="mx-auto max-w-3xl"
    >
      <StorefrontCard className="p-5 sm:p-6">
        <Markdown content={content} className="text-sm leading-relaxed text-slate-700 space-y-4" />
      </StorefrontCard>
    </StorefrontPage>
  )
}
